import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert, CircularProgress,
  FormControlLabel, Switch, Divider, InputAdornment, IconButton
} from '@mui/material';
import { Email, Visibility, VisibilityOff, Send, CheckCircle } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ConfiguracionEmail() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [config, setConfig] = useState({
    host: '', port: '587', user: '', password: '', secure: false, fromName: ''
  });
  const [configurado, setConfigurado] = useState(false);
  const [emailPrueba, setEmailPrueba] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargarConfig(); }, []);

  const cargarConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/email/config`, { headers: headers() });
      if (res.data.configurado) {
        setConfig({ ...res.data, password: '' });
        setConfigurado(true);
      }
    } catch {} finally { setLoading(false); }
  };

  const guardar = async () => {
    if (!config.host || !config.user || !config.password) {
      setMensaje({ tipo: 'error', texto: 'Host, usuario y contraseña son requeridos' });
      return;
    }
    setSaving(true); setMensaje(null);
    try {
      await axios.post(`${API_URL}/api/email/config`, config, { headers: headers() });
      setMensaje({ tipo: 'success', texto: '✅ Configuración SMTP guardada correctamente' });
      setConfigurado(true);
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al guardar' });
    } finally { setSaving(false); }
  };

  const verificar = async () => {
    setSaving(true); setMensaje(null);
    try {
      await axios.post(`${API_URL}/api/email/verificar`, {}, { headers: headers() });
      setMensaje({ tipo: 'success', texto: '✅ Conexión SMTP verificada correctamente' });
    } catch (e) {
      setMensaje({ tipo: 'error', texto: `❌ Error de conexión: ${e.response?.data?.error || e.message}` });
    } finally { setSaving(false); }
  };

  const enviarPrueba = async () => {
    if (!emailPrueba) { setMensaje({ tipo: 'error', texto: 'Ingrese un email para la prueba' }); return; }
    setSaving(true); setMensaje(null);
    try {
      const res = await axios.post(`${API_URL}/api/email/enviar-prueba`, { destinatario: emailPrueba }, { headers: headers() });
      setMensaje({ tipo: 'success', texto: `✅ ${res.data.message}` });
    } catch (e) {
      setMensaje({ tipo: 'error', texto: `❌ ${e.response?.data?.error || e.message}` });
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Email color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Configuración de Email</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure su servidor SMTP para enviar facturas por correo electrónico
          </Typography>
        </Box>
        {configurado && <CheckCircle color="success" sx={{ ml: 'auto', fontSize: 28 }} />}
      </Box>

      {mensaje && <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje(null)}>{mensaje.texto}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Servidor SMTP</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ingrese los datos de su servidor de correo. Si usa Gmail, active "Contraseñas de aplicación" en su cuenta Google.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Host SMTP" value={config.host}
              onChange={e => setConfig({ ...config, host: e.target.value })}
              placeholder="smtp.gmail.com" helperText="Ej: smtp.gmail.com, smtp.outlook.com, mail.tudominio.com" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Puerto" value={config.port}
              onChange={e => setConfig({ ...config, port: e.target.value })}
              placeholder="587" helperText="587 (TLS) o 465 (SSL)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Usuario / Email" value={config.user}
              onChange={e => setConfig({ ...config, user: e.target.value })}
              placeholder="facturacion@tuempresa.com" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contraseña" type={showPassword ? 'text' : 'password'}
              value={config.password}
              onChange={e => setConfig({ ...config, password: e.target.value })}
              placeholder={configurado ? '(sin cambios)' : 'Contraseña SMTP'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nombre del remitente" value={config.fromName}
              onChange={e => setConfig({ ...config, fromName: e.target.value })}
              placeholder="Mi Empresa S.A.S" helperText="Nombre que aparece en el correo" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={config.secure} onChange={e => setConfig({ ...config, secure: e.target.checked })} />}
              label="Conexión SSL/TLS (puerto 465)" />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando...' : '💾 Guardar Configuración'}
          </Button>
          <Button variant="outlined" onClick={verificar} disabled={saving || !configurado}>
            🔌 Verificar Conexión
          </Button>
        </Box>
      </Paper>

      {/* Enviar prueba */}
      {configurado && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Enviar Email de Prueba</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verifique que los correos se envían correctamente
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField size="small" label="Email destino" value={emailPrueba}
              onChange={e => setEmailPrueba(e.target.value)}
              placeholder="tucorreo@ejemplo.com" sx={{ flex: 1 }} />
            <Button variant="outlined" startIcon={<Send />} onClick={enviarPrueba} disabled={saving}>
              Enviar Prueba
            </Button>
          </Box>
        </Paper>
      )}

      {/* Guía rápida */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom>📋 Guía rápida por proveedor:</Typography>
        <Grid container spacing={2} sx={{ fontSize: '0.85rem' }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2"><strong>Gmail:</strong></Typography>
            <Typography variant="caption" display="block">Host: smtp.gmail.com</Typography>
            <Typography variant="caption" display="block">Puerto: 587</Typography>
            <Typography variant="caption" display="block">Usar "Contraseña de aplicación"</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2"><strong>Outlook/Hotmail:</strong></Typography>
            <Typography variant="caption" display="block">Host: smtp.office365.com</Typography>
            <Typography variant="caption" display="block">Puerto: 587</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2"><strong>Dominio propio:</strong></Typography>
            <Typography variant="caption" display="block">Host: mail.tudominio.com</Typography>
            <Typography variant="caption" display="block">Puerto: 587 o 465</Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
